import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ExternalLink } from 'lucide-react';

import { brand } from '@brand';
import { useClinicalState } from '@/lib/api/hooks';
import { Icon as BrandIcon } from '@/lib/Icon';
import { storeProductImageUrls } from '@/lib/storeProductImage';
import { orderWaitLabel, useOrderWait } from '../OrderProgress';
import type { Order } from '@/types/api';

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/**
 * OrderRow — mock-matched layout: square product thumbnail on the left,
 * two-row content area (header line: order number + status pill, then
 * the SKU title), price · date · Track ↗ row, and a "View details ▶"
 * footer bar that links into the order detail page.
 *
 * Status labels are uppercased + spaces (PENDING REVIEW etc.). New
 * states surfaced by the eRx-first flow get user-readable labels here
 * — the raw enum (awaiting_prescription, awaiting_pharmacy_routing,
 * superseded) would otherwise leak.
 */
const STATUS_LABEL: Record<string, string> = {
  awaiting_prescription: 'AWAITING RX',
  awaiting_pharmacy_routing: 'AWAITING ROUTING',
  pending_review: 'PENDING REVIEW',
  pending_approval: 'PENDING APPROVAL',
  processing: 'PROCESSING',
  shipped: 'SHIPPED',
  delivered: 'DELIVERED',
  cancelled: 'CANCELLED',
  failed: 'FAILED',
};

function statusLabel(status: string, awaitingConsult: boolean): string {
  // While the order is held by the consult gate — booked OR not —
  // surface "AWAITING CONSULT" so the patient knows the visit is the
  // blocker, not Hinds's keyboard. Only flips back to "AWAITING RX"
  // after the visit actually occurs.
  if (status === 'awaiting_prescription' && awaitingConsult) {
    return 'AWAITING CONSULT';
  }
  return STATUS_LABEL[status] ?? status.replace(/_/g, ' ').toUpperCase();
}

export function OrderRow({
  order,
  storeImageStyle,
  eager = false,
}: {
  order: Order;
  /** Storefront build: the active store's product artwork style
   *  (`bootstrap.storefront.product_image_style`). */
  storeImageStyle?: string | null;
  /** The first row's image is the page's largest paint: load it at once. */
  eager?: boolean;
}) {
  const { data: clinical } = useClinicalState();
  const awaitingConsult = Boolean(clinical?.awaiting_consult);
  const created = parseISO(order.created_at);
  const primary = order.lines[0];
  const primaryName = primary?.name ?? order.order_number;
  // Storefront build: the store's own artwork for the canonical product in
  // its image style, then the clinical-white render, then the vial drawing
  // (no store artwork in the house build).
  const storeImageUrls = brand.productImages
    ? []
    : storeProductImageUrls(primary?.product_id, storeImageStyle, { small: true });
  const storeImageKey = storeImageUrls.join('|');
  const [storeImageFailures, setStoreImageFailures] = useState({ key: storeImageKey, count: 0 });
  const storeImageIndex =
    storeImageFailures.key === storeImageKey ? storeImageFailures.count : 0;
  const storeImageUrl = storeImageUrls[storeImageIndex] ?? null;
  const heroUrl = brand.productImages ? (primary?.hero_image_url ?? null) : storeImageUrl;
  // Mirror the cancelled-subscription treatment: de-emphasize cancelled
  // orders with a subtle background + reduced opacity (see SubscriptionsSection).
  const isLocked = order.status === 'cancelled';
  // Show what the patient actually paid (after account credit / intro discount)
  // with the list price struck through. Falls back to total when unresolved.
  const paidCents = order.amount_paid_cents ?? order.total_cents;
  // Storefront: the row link is named short ("View order RX-AD925C32"),
  // and the product, status, amount and date are its description, not every
  // string in the card run together as its name.
  const uid = useId();
  const describedBy = brand.orderReviewSteps
    ? ['name', 'status', 'amount', 'paid', 'date'].map((part) => `${uid}-${part}`).join(' ')
    : undefined;
  const partId = (part: string) => (brand.orderReviewSteps ? `${uid}-${part}` : undefined);

  return (
    <div
      className={`border border-line-subtle overflow-hidden ${isLocked ? 'bg-surface-subtle opacity-45' : 'bg-[var(--surface-page)]'}`}
    >
      <Link
        to={`/app/orders/${order.id}`}
        className="block"
        aria-label={brand.orderReviewSteps ? `View order ${order.order_number}` : undefined}
        aria-describedby={describedBy}
      >
        <div className="flex items-stretch">
          <div className="w-[8rem] shrink-0 bg-[var(--surface-sunken)] aspect-square flex items-center justify-center overflow-hidden">
            {heroUrl ? (
              <img
                src={heroUrl}
                alt={brand.orderReviewSteps ? '' : primaryName}
                width={220}
                height={220}
                className="w-full h-full object-cover"
                loading={eager ? 'eager' : 'lazy'}
                fetchPriority={eager ? 'high' : undefined}
                onError={
                  storeImageUrl
                    ? () =>
                        setStoreImageFailures({
                          key: storeImageKey,
                          count: storeImageIndex + 1,
                        })
                    : undefined
                }
              />
            ) : (
              <svg
                viewBox="0 0 64 80"
                width="44"
                height="56"
                fill="none"
                aria-hidden
                className="text-ink-muted"
              >
                <rect x="22" y="2" width="20" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
                <rect x="14" y="10" width="36" height="68" rx="3" stroke="currentColor" strokeWidth="1.4" />
                <line x1="14" y1="20" x2="50" y2="20" stroke="currentColor" strokeWidth="1" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col bg-raised">
            <div className="bg-sunken flex justify-between gap-10 flex-wrap">
              <span id={partId('number')} className="type-label-sm text-ink-muted py-10 px-15 flex-1">
                {order.order_number}
              </span>
              {brand.orderReviewSteps ? (
                <StorefrontOrderChip order={order} id={partId('status')} />
              ) : (
                <span className="type-label-xs flex items-center justify-center min-w-[12rem] text-ink-secondary bg-[var(--accent)] px-10 py-5 text-white">
                  {statusLabel(order.status, awaitingConsult)}
                </span>
              )}
            </div>
            <div className="px-15 py-15 gap-10 flex flex-col">
              <h3 id={partId('name')} className="type-title-sm leading-tight truncate">
                {primaryName}
              </h3>
              <div className="flex items-baseline justify-between gap-15 type-body-sm text-ink-muted">
                <span>
                  <span id={partId('amount')} className="type-data-sm text-ink">
                    {dollars(paidCents)}
                  </span>
                  <span id={partId('paid')} className="type-label-xs text-ink-muted ml-5">
                    {order.amount_paid_cents == null
                      ? 'Order total'
                      : brand.planOrderPayments
                        ? 'Paid'
                        : 'Invoice / charge paid'}
                  </span>
                  <span className="text-line-DEFAULT mx-5" aria-hidden={brand.orderReviewSteps || undefined}>
                    |
                  </span>
                  {brand.orderReviewSteps ? (
                    <span id={partId('date')}>{format(created, 'MMM d, yyyy')}</span>
                  ) : (
                    format(created, 'MMM d, yyyy')
                  )}
                </span>
                {order.shipment?.tracking_url && (
                  <a
                    href={order.shipment.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-5 type-label-sm text-ink-muted hover:text-ink"
                  >
                    Track <ExternalLink size={11} strokeWidth={1.5} />
                  </a>
                )}
              </div>
              {order.issue && (
                <div className="type-body-sm text-warning">{order.issue}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-15 py-12 border-t border-line-subtle bg-[var(--slate)] type-body-md text-ink">
          View details
          <span aria-hidden className="inline-flex items-center justify-center w-8 text-ink-muted"><BrandIcon name="Arrow" viewBox="0 0 10 12" /></span>
        </div>
      </Link>
    </div>
  );
}

/** Storefront: what the order waits on, in plain words; amber when it is
 *  the patient's move, neutral otherwise. */
function StorefrontOrderChip({ order, id }: { order: Order; id?: string }) {
  const { wait } = useOrderWait(order);
  const onPatient = wait === 'intake' || wait === 'id' || wait === 'visit';
  return (
    <span
      id={id}
      className={`type-label-xs flex items-center justify-center min-w-[12rem] px-10 py-5 ${
        onPatient ? 'bg-warning-wash text-ink' : 'bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)]'
      }`}
    >
      {orderWaitLabel(wait, order.status)}
    </span>
  );
}
